import { createLegacyApiClient, createLegacyApiMiddleware, LegacyApiMiddlewareProps } from '@repo/legacy-commerce-api';
import { push } from 'redux-first-history';
import * as actions from '../actions';
import { CLEAR_BASKET_INFO } from '../constants';
import config from '../constants/config';
import { store } from '../index';
import { login } from '../utils/keycloak';
import { removeBasketBeforeLogin, scrollToTop } from '../utils/react';

const BASKET_LOGIN_ATTEMPT_KEY = 'basket_login_attempt';
let basketLoginPending = false;

/**
 * Refs: https://dreipol.atlassian.net/browse/APG-155
 * Creates a middleware for the legacy-api/commerce.
 * This used to be the apiCaller.ts in the legacy system. All logic remains the same and was ported over as is.
 */
const legacyApi = createLegacyApiClient(config.apiUrl);

const middlewareConfig: LegacyApiMiddlewareProps = {
  getLocale: () => store.getState().intl.locale,
  getCredentials: () => {
    let keycloakToken = null;
    let keycloakRefreshToken = null;
    if (store.getState().app.keycloakReducer.tokens) {
      keycloakToken = store.getState().app.keycloakReducer.tokens.token;
      keycloakRefreshToken = store.getState().app.keycloakReducer.tokens.refreshToken;
    }
    const apiToken = keycloakToken || (window as any).sessionStorage.getItem('bearerTokenEC');
    const refreshToken = keycloakRefreshToken || (window as any).sessionStorage.getItem('refreshTokenEC');
    return {
      accessToken: apiToken,
      refreshToken: refreshToken,
      clientHash: (window as any).clientHash,
    };
  },
  setCredentials: (credentials) => {
    if (credentials.accessToken) (window as any).sessionStorage.setItem('bearerTokenEC', credentials.accessToken);
    if (credentials.refreshToken) (window as any).sessionStorage.setItem('refreshTokenEC', credentials.refreshToken);
  },
  scrollToTop: () => scrollToTop(),
  setNotice: (notice) => {
    store.dispatch(actions.receivedNotice({ ...notice }));
  },
  onSearchError: (error) => {
    store.dispatch(
      push({
        search: error,
      }),
    );
  },
  onBasketAccessDenied: ({ anonymous }) => {
    // Parallel basket requests fail together; the first one already started the login redirect
    if (basketLoginPending) return;

    const basket = store.getState().app.userDataEC.basketInfo?.basket;

    // An expired login can still own the basket: log in once to recover it, otherwise drop it
    if (anonymous && basket && sessionStorage.getItem(BASKET_LOGIN_ATTEMPT_KEY) !== String(basket)) {
      sessionStorage.setItem(BASKET_LOGIN_ATTEMPT_KEY, String(basket));
      basketLoginPending = true;
      login();
      return;
    }

    store.dispatch({ type: CLEAR_BASKET_INFO });
    removeBasketBeforeLogin();
  },
  onBasketAccessGranted: ({ anonymous }) => {
    // Anonymous reads of an owned basket still succeed, so only a logged-in success proves the login recovered it
    if (!anonymous) {
      sessionStorage.removeItem(BASKET_LOGIN_ATTEMPT_KEY);
    }
  },
};

// Instantiate the middleware according to the config, see createLegacyApiMiddleware inside legacy-commerce-api for details:
legacyApi.use(createLegacyApiMiddleware(middlewareConfig));

export default legacyApi;
